import { Middleware } from '@nuxt/types'
import { getCookieDomain } from '@/utils/cookie'
import { AttributionCookie } from '@/enums/gtm'

const CAMPAIGN_TIMEOUT = 6 * 30 * 24 * 60 * 60 * 1000

function getLastValue<T> (value: T | T[]): T {
  if (Array.isArray(value)) {
    return value[value.length - 1]
  }

  return value
}

function removeTld (domain: string): string {
  if (!domain) {
    return domain
  }

  const popularTlds = [
    '.com', '.net', '.org', '.edu', '.gov', '.mil', '.int',
    '.nl', '.de', '.uk', '.fr', '.it', '.es', '.pt', '.be',
    '.at', '.ch', '.dk', '.no', '.se', '.fi', '.pl', '.cz',
    '.hu', '.ro', '.bg', '.hr', '.si', '.sk', '.lt', '.lv',
    '.ee', '.ie', '.mt', '.cy', '.lu', '.li', '.mc', '.sm',
    '.va', '.ad', '.co', '.io', '.ai', '.tv', '.me', '.ly',
    '.app', '.dev', '.tech', '.online', '.site', '.web',
    '.blog', '.shop', '.store', '.info', '.biz', '.pro'
  ]

  let cleanedDomain = domain.toLowerCase()

  if (cleanedDomain.startsWith('www.')) {
    cleanedDomain = cleanedDomain.substring(4)
  }

  for (const tld of popularTlds) {
    if (cleanedDomain.includes(tld)) {
      cleanedDomain = cleanedDomain.replace(new RegExp(tld, 'g'), '')
    }
  }

  return cleanedDomain
}

function getUtmParams (query: any) {
  if (query.gclid || query.dclid) {
    return {
      source: 'google',
      medium: 'cpc',
      campaign: getLastValue(query.utm_campaign) || '(not set)',
      content: getLastValue(query.utm_content) || '(not set)',
      term: getLastValue(query.utm_term) || '(not set)',
      hasAutoTagging: true
    }
  }

  if (query.fbclid) {
    return {
      source: 'facebook',
      medium: 'social',
      campaign: getLastValue(query.utm_campaign) || '(not set)',
      content: getLastValue(query.utm_content) || '(not set)',
      term: getLastValue(query.utm_term) || '(not set)',
      hasAutoTagging: true
    }
  }

  return {
    source: getLastValue(query.utm_source) || '',
    medium: getLastValue(query.utm_medium) || '',
    campaign: getLastValue(query.utm_campaign) || '',
    content: getLastValue(query.utm_content) || '',
    term: getLastValue(query.utm_term) || '',
    hasAutoTagging: false
  }
}

function getReferrerAttribution (referrer: string) {
  if (!referrer) {
    return { source: '(direct)', medium: '(none)' }
  }

  try {
    const url = new URL(referrer)
    let hostname = url.hostname.toLowerCase()

    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4)
    }

    const organicSearchEngines = [
      'google', 'bing', 'yahoo', 'baidu', 'duckduckgo', 'yandex', 'ask', 'aol', 'msn', 'seznam', 'qwant', 'ecosia'
    ]

    const socialPlatforms = [
      'facebook.com', 'm.facebook.com', 'l.facebook.com', 'twitter.com', 'x.com', 'linkedin.com', 'instagram.com',
      'pinterest.com', 'youtube.com', 'tiktok.com', 'reddit.com', 'tumblr.com', 'snapchat.com'
    ]

    if (organicSearchEngines.some(engine => hostname.includes(engine))) {
      return { source: removeTld(hostname), medium: 'organic' }
    }

    if (socialPlatforms.some(platform => hostname.endsWith(platform))) {
      return { source: removeTld(hostname.split('.').slice(-2, -1)[0]) || hostname, medium: 'social' }
    }

    if (hostname.includes('localhost') || hostname.includes('onthatass.com') || hostname.includes('onthatass.dev')) {
      return { source: '(direct)', medium: '(none)' }
    }

    return { source: hostname, medium: 'referral' }
  } catch {
    return { source: '(direct)', medium: '(none)' }
  }
}

const attributionMiddleware: Middleware = ({ route, app, req }) => {
  try {
    if (process.server && req) {
      const query = route.query
      const referrer = req.headers.referer || ''
      const cookieDomain = getCookieDomain()
      const cookies = app.$cookies.get(AttributionCookie)
      let existingAttribution = null

      if (cookies) {
        try {
          existingAttribution = cookies
        } catch {}
      }

      const utmParams = getUtmParams(query)
      const currentTime = Date.now()
      const currentReferrerAttribution = getReferrerAttribution(referrer)
      let shouldUpdateAttribution = false
      let newAttribution = null

      if (!existingAttribution || !existingAttribution.source) {
        shouldUpdateAttribution = true
      } else if (
        utmParams.source ||
        utmParams.medium ||
        utmParams.campaign ||
        utmParams.content ||
        utmParams.term ||
        utmParams.hasAutoTagging
      ) {
        shouldUpdateAttribution = true
      } else if (currentReferrerAttribution.source !== '(direct)' && currentReferrerAttribution.medium !== '(none)') {
        shouldUpdateAttribution = true
      }

      if (shouldUpdateAttribution) {
        let source, medium, campaign, content, term
        if (utmParams.source) {
          source = utmParams.source
          medium = utmParams.medium || '(not set)'
          campaign = utmParams.campaign || '(not set)'
          content = utmParams.content || '(not set)'
          term = utmParams.term || '(not set)'
        } else {
          source = currentReferrerAttribution.source
          medium = currentReferrerAttribution.medium
          campaign = '(not set)'
          content = '(not set)'
          term = '(not set)'
        }
        newAttribution = { source, medium, campaign, content, term, timestamp: currentTime }
      } else {
        newAttribution = { ...existingAttribution, timestamp: currentTime }
      }

      app.$cookies.set(
        AttributionCookie,
        JSON.stringify(newAttribution),
        {
          expires: new Date(Date.now() + CAMPAIGN_TIMEOUT),
          path: '/',
          domain: cookieDomain,
          secure: true,
          sameSite: 'lax',
          httpOnly: false
        }
      )
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error in attribution middleware: ${error}`)
  }
}

export default attributionMiddleware
